<template>
  <div v-if="showSetting" class="default-setting">
    <file-basic-setting
      ref="basicSettingRef"
      v-model="form.file"
      class="m-b-24"
      @changeModel="onChangeModel"
    />
    <!-- 商务文件的目录 -->
    <file-catalog-setting
      ref="catalogSettingRef"
      v-model="form.catalogList"
      :review-model="reviewModel"
      :bid-file-type="bidFileTypeDict.BUSINESS.value"
      class="m-b-24"
    />
    <!-- 报价文件的目录 -->
    <file-catalog-setting
      v-show="isDouble"
      ref="quotationCatalogSetting"
      v-model="form.quotationCatalogList"
      :review-model="reviewModel"
      :bid-file-type="bidFileTypeDict.QUOTATION.value"
      class="m-b-24"
    />
    <bid-review-list-setting
      ref="reviewListSettingRef"
      :review-list="form.wayList"
      :catalog-list="form.catalogList"
      :is-score-once.sync="form.file.isScoreOnce"
      :decimal-point.sync="form.file.decimalPoint"
      class="m-b-24"
      :kpb-file-id="kpbFileId"
      :review-model="reviewModel"
      :review-method="reviewMethod"
      @changeIsScoreOnce="changeIsScoreOnce"
      @export="exportExcel"
      @getReviewWayOption="getReviewWayOption"
    />
    <file-open-setting
      ref="openSettingRef"
      v-model="form.markList"
      class="m-b-24"
      :review-model="reviewModel"
      :bid-file-type="bidFileTypeDict.BUSINESS.value"
    />
    <file-open-setting
      v-show="isDouble"
      ref="quotationOpenSettingRef"
      v-model="form.quotationMarkList"
      class="m-b-24"
      :review-model="reviewModel"
      :bid-file-type="bidFileTypeDict.QUOTATION.value"
    />
    <!-- 生成招标文件 -->
    <div class="card file-upload-setting">
      <div class="section">
        <div class="section-title">
          <base-section-title title="上传招标文件" />
        </div>
        <div class="section-content">
          <upload-authorization-file
            ref="UploadAuthorizationFile"
            v-model="form.file.biddingAttachmentIds"
            upload-mode="fileId"
            :preview-api="previewBiddingFileApi"
            :download-api="downloadBiddingFileApi"
            :request-params="{
              orderId: orderId,
              fileType: fileTypeDict.KPB_BID.value,
              fileId: form.file.biddingAttachmentIds
            }"
            :props-data="{
              beforeUpload: handleBeforeUpload,
              limit: 1
            }"
          >
            <template slot="tip">
              <div class="important-tip m-l-10">
                仅支持pdf类型文件上传，单个文件最大50MB；其他类型文件单个最大200MB
              </div>
            </template>
          </upload-authorization-file>
        </div>
      </div>
    </div>

    <div class="page-footer flex col-center row-right p-r-24">
      <el-button @click="onCancel">返回</el-button>
      <el-button @click="onSave">保存至草稿</el-button>
      <el-button type="primary" @click="onSubmit">提交</el-button>
    </div>
  </div>
</template>

<script>
import FileBasicSetting from '@/components/kpb/project/FileBasicSetting.vue'
import FileCatalogSetting from '@/components/kpb/project/FileCatalogSetting.vue'
import BidReviewListSetting from '@/components/kpb/project/BidReviewListSetting.vue'
import FileOpenSetting from '@/components/kpb/project/FileOpenSetting.vue'
import UploadAuthorizationFile from '@/components/common/Upload/UploadAuthorizationFile.vue'
import BaseSectionTitle from '@/components/common/BaseSectionTitle.vue'
import {
  downloadBiddingFileApi,
  fetchInviteBidDetailApi,
  previewBiddingFileApi
} from '@/api/procure/invite-bid'
import { fileTypeDict } from '@/dictionary/project/bid'
import {
  bidFileTypeDict,
  bidFileDirectoryTypeDict,
  isScoreOnceDict,
  kpbBidFileStatusDict,
  reviewMethodModelDict,
  reviewModelDict,
  reviewWayTypeDict,
  bidReviewScoringTypeDict
} from '@/dictionary/project/kpb-project'
import {
  exportReviewWaysExcelApi,
  getKpbBiddingFileApi,
  saveKpbBiddingFileApi,
  updateKpbBiddingFileApi
} from '@/api/procure/kpb-project'
import { cloneDeep } from 'lodash'

/**
 * @description 默认的单个文件加密的电子招标文件设置组件
 */
export default {
  name: 'BidFileSetting',

  components: {
    BaseSectionTitle,
    UploadAuthorizationFile,
    FileBasicSetting,
    FileCatalogSetting,
    BidReviewListSetting,
    FileOpenSetting
  },

  data() {
    return {
      fileTypeDict,
      bidFileTypeDict,

      showSetting: false,

      form: {
        // 电子标书状态
        fileStatus: kpbBidFileStatusDict.INIT.value,
        // 文件信息
        file: {
          biddingAttachmentIds: [],
          // 是否启用了同时打分
          isScoreOnce: '',
          // 评审模式
          reviewModel: reviewModelDict.DEFAULT.value,
          // 评审办法
          reviewMethod: reviewMethodModelDict.DEFAULT.value,
          // 评分精度
          decimalPoint: 4
        },
        // 目录列表
        catalogList: [],
        // 报价文件目录列表
        quotationCatalogList: [],
        // 开标一览表
        markList: [],
        // 报价开标一览表
        quotationMarkList: [],
        // 评审办法列表
        wayList: [],
        // 报价文件目录
        quoteCatalogList: []
      }
    }
  },

  computed: {
    orderId() {
      return Number(this.$route.params.orderId || 0)
    },

    projectId() {
      return Number(this.$route.params.projectId || 0)
    },

    kpbFileId() {
      return Number(this.$route.params.kpbFileId || 0)
    },

    // 仅针对澄清提交页上传的澄清文件附件，暂存id
    fileChangeGroupId() {
      return Number(this.$route.query.fileChangeGroupId || 0)
    },

    lastKpbFileId() {
      return Number(this.$route.params.lastKpbFileId || 0)
    },

    lastRouteName() {
      return this.$route.params.lastRoute
    },

    // 是否是双信封评审模式
    isDouble() {
      return this.form.file.reviewModel === reviewModelDict.DOUBLE.value
    },

    reviewModel() {
      return this.form.file.reviewModel || reviewModelDict.DEFAULT.value
    },

    reviewMethod() {
      return this.form.file.reviewMethod
    }
  },

  created() {
    if (this.lastKpbFileId) {
      // 先查询当前的电子标书信息
      this.getKpbBiddingFile(this.kpbFileId).then(res => {
        // 如果电子标书状态为初始状态，则继续请求上一个电子标书的信息
        if (
          Number(res.data?.fileStatus || 0) ===
          Number(kpbBidFileStatusDict.PRE.value)
        ) {
          // 此时不能使用上一个电子标书的附件信息
          this.getKpbBiddingFile(this.lastKpbFileId, false)
        }
      })
      return
    }
    if (this.lastRouteName?.includes('Changes')) {
      // 先查询当前的电子标书信息
      this.getKpbBiddingFile(this.kpbFileId).then(res => {
        if (
          Number(res.data?.fileStatus || 0) ===
          Number(kpbBidFileStatusDict.INIT.value)
        ) {
          this.getBidOrderDetail()
        }
      })
    } else {
      this.getKpbBiddingFile(this.kpbFileId)
    }
  },

  methods: {
    previewBiddingFileApi,
    downloadBiddingFileApi,

    handleBeforeUpload(file) {
      const _type = file.name.split('.').pop()
      if (_type.toLowerCase() !== 'pdf') {
        this.$message.error('只允许上传pdf文件')
        return false
      }
      return true
    },

    onCancel() {
      this.handleBackFun()
    },

    onSave() {
      this.validateSettingForm().then(() => {
        const submitData = this.handleSubmitData(this.form)
        saveKpbBiddingFileApi(this.kpbFileId, submitData)
          .then(() => {
            this.$message.success('已保存至草稿')
          })
          .catch(err => {
            this.$message.error(err?.message)
          })
      })
    },

    onSubmit() {
      this.validateSettingForm(true).then(() => {
        this.$antConfirm({
          title: '确定提交电子标书信息吗？',
          okText: '确定',
          cancelText: '取消',
          icon: 'exclamation-circle',
          centered: true,
          onOk: () => {
            const submitData = this.handleSubmitData(this.form)
            updateKpbBiddingFileApi(this.kpbFileId, submitData)
              .then(res => {
                this.handleBackFun()
              })
              .catch(err => {
                if (err?.message) this.$message.error(err.message)
              })
          }
        })
      })
    },

    validateFile() {
      return new Promise((resolve, reject) => {
        // 招标文件已上传
        const hasFile = !!this.form.file.biddingAttachmentIds?.length > 0
        if (hasFile) {
          resolve()
        } else {
          this.$message.error('请上传招标文件')
          reject()
        }
      })
    },

    validateSettingForm(validateAll = false) {
      const validateList = validateAll
        ? [
          this.$refs.basicSettingRef.validateSettingForm(),
          this.$refs.catalogSettingRef.validateSettingForm(),
          this.$refs.reviewListSettingRef.validateWayList(),
          this.validateFile()
        ]
        : [
          this.$refs.catalogSettingRef.validateSettingForm(),
          this.$refs.reviewListSettingRef.validateWayList()
        ]
      return new Promise(resolve => {
        Promise.allSettled(validateList).then(res => {
          if (!this.form.file.decimalPoint) {
            this.$message.warning('请选择评分设置')
            return
          }
          // 全部表单校验通过
          const allFormValidPass = res.every(
            item => item.status === 'fulfilled'
          )
          if (allFormValidPass) {
            resolve()
          }
        })
      })
    },

    /**
     * 将页面数据转换成可提交数据
     */
    handleSubmitData(form) {
      this.form.wayList = this.$refs.reviewListSettingRef.getReviewListSetting()
      // 后端将商务目录和报价目录合并，根据bidFileType区分，与页面设计冲突
      // 为方便前端开发，在页面交互时拆为两个数组进行区分，在提交时合并
      const defaultCatalog = this.handleCatalogListBeforeSubmit(
        form.catalogList
      )
      const quoteCatalog = this.handleQuoteCatalogListBeforeSubmit(
        form.quotationCatalogList
      )
      const catalogList = this.isDouble
        ? [...defaultCatalog, ...quoteCatalog]
        : [...defaultCatalog]
      // 开标一览表同理
      const markList = this.isDouble
        ? [...form.markList, ...form.quotationMarkList]
        : [...form.markList]
      // 双信封评审，不能同时评审所有打分型评审办法
      if (this.isDouble) {
        form.file.isScoreOnce = isScoreOnceDict.NO.value
      }
      const result = {
        file: form.file,
        catalogList: catalogList,
        markList: markList,
        wayList: form.wayList.map((item, index) => {
          item.orderNum = index + 1
          item.itemList = (item.itemList || []).map((wayItem, wayIndex) => {
            wayItem.orderNum = wayIndex + 1
            return wayItem
          })
          // 针对手动算分的评审办法，需要手动清除自动算分信息，保证数据正确
          const isManual =
            item.scoringType === bidReviewScoringTypeDict.MANUAL.value
          if (isManual) {
            item.autoScoring = {}
          }
          return item
        })
      }
      return result
    },

    changeIsScoreOnce(val) {
      this.form.file.isScoreOnce = val
    },

    handleCatalogListBeforeSubmit(catalogList) {
      const tempCatalogList = []
      // 在商务文件中手动添加开标一览表
      const bidOpenCatalog = {
        orderNum: 0,
        bidFileType: bidFileTypeDict.BUSINESS.value,
        catalogName: bidFileDirectoryTypeDict.MARK.label,
        uploadRequire: false,
        signRequire: false,
        catalogType: bidFileDirectoryTypeDict.MARK.value
      }
      const hasMarkCatalog =
        catalogList.findIndex(
          item => item.catalogType === bidFileDirectoryTypeDict.MARK.value
        ) > -1
      if (!hasMarkCatalog) {
        tempCatalogList.push(bidOpenCatalog)
      }
      // 如果开启分项报价，则添加分项报价目录
      const itemizedCatalog = {
        orderNum: 0,
        bidFileType: bidFileTypeDict.BUSINESS.value,
        catalogName: bidFileDirectoryTypeDict.ITEMIZED.label,
        uploadRequire: false,
        signRequire: false,
        catalogType: bidFileDirectoryTypeDict.ITEMIZED.value
      }
      const hasItemizedCatalog =
        catalogList.findIndex(
          item => item.catalogType === bidFileDirectoryTypeDict.ITEMIZED.value
        ) > -1
      if (this.form.file.isEnableItemOffer && !hasItemizedCatalog) {
        tempCatalogList.push(itemizedCatalog)
      }
      tempCatalogList.push(...catalogList)
      return tempCatalogList
    },

    handleQuoteCatalogListBeforeSubmit(catalogList) {
      const tempCatalogList = []
      // 在报价文件中添加报价开标一览表
      const quoteOpenCatalog = {
        orderNum: 0,
        bidFileType: bidFileTypeDict.QUOTATION.value,
        catalogName: bidFileDirectoryTypeDict.MARK.label,
        uploadRequire: false,
        signRequire: false,
        catalogType: bidFileDirectoryTypeDict.MARK.value
      }
      const hasMarkCatalog =
        catalogList.findIndex(
          item => item.catalogType === bidFileDirectoryTypeDict.MARK.value
        ) > -1
      if (!hasMarkCatalog) {
        tempCatalogList.push(quoteOpenCatalog)
      }
      tempCatalogList.push(...catalogList)
      return tempCatalogList
    },

    handleBackFun() {
      if (!this.lastRouteName || this.lastRouteName.includes('JZ')) {
        window.close()
      }
      const params = {
        kpbFileId: this.kpbFileId,
        kpbGroupId: this.form.file.biddingAttachmentIds
      }
      if (this.orderId) params.orderId = this.orderId
      if (this.projectId) params.projectId = this.projectId
      if (this.fileChangeGroupId) {
        params.fileChangeGroupId = this.fileChangeGroupId
      }

      this.$router.replace({
        name: this.lastRouteName,
        params
      })
    },

    getKpbBiddingFile(kpbFileId, dontUseAttachmentGroupId = false) {
      this.showSetting = false
      if (!kpbFileId) return Promise.resolve()
      return getKpbBiddingFileApi(kpbFileId)
        .then(res => {
          const data = cloneDeep(res.data || {})
          this.form.file = Object.assign({}, this.form.file, data.file)
          // 评审模式和评审办法在初始化时，后端没有值，返回0，会造成交互问题
          if (!this.form.file.reviewModel) {
            this.form.file.reviewModel = reviewModelDict.DEFAULT.value
            this.form.file.reviewMethod = reviewMethodModelDict.DEFAULT.value
          }
          if (!this.form.file.decimalPoint) {
            this.form.file.decimalPoint = undefined
          }
          if (!data.file.biddingAttachmentIds) {
            this.form.file.biddingAttachmentIds = []
          }

          if (data.catalogList?.length) {
            // 商务文件目录
            this.form.catalogList = data.catalogList.filter(
              item => item.bidFileType === bidFileTypeDict.BUSINESS.value
            )
            // 报价文件目录
            this.form.quotationCatalogList = data.catalogList.filter(
              item => item.bidFileType === bidFileTypeDict.QUOTATION.value
            )
          }
          if (data.markList?.length) {
            // 商务开标一览表
            this.form.markList = data.markList.filter(
              item => item.bidFileType === bidFileTypeDict.BUSINESS.value
            )
            // 报价开标一览表
            this.form.quotationMarkList = data.markList.filter(
              item => item.bidFileType === bidFileTypeDict.QUOTATION.value
            )
          }
          if (data.wayList?.length) {
            this.form.wayList = data.wayList.map(way => {
              way.title =
                reviewWayTypeDict(this.reviewModel).translate(way.wayType)
                  ?.label || ''
              way.itemList.forEach(item => {
                item.relationCatalog.catalogName =
                  data.catalogList.find(catalog => {
                    return catalog.orderNum === item.relationCatalog.orderNum
                  })?.catalogName || ''
              })
              return way
            })
          }
          if (dontUseAttachmentGroupId) {
            this.form.file.biddingAttachmentIds = []
          }
          if (!data.file.recommendCandidateNum) {
            this.form.file.recommendCandidateNum = undefined
          }
          return res
        })
        .catch(err => {
          console.log('查询电子标书详情 err', err)
          if (err?.message) this.$message.error(err.message)
        })
        .finally(() => {
          this.showSetting = true
        })
    },

    /**
     * 获取招标单详情
     */
    getBidOrderDetail() {
      fetchInviteBidDetailApi({ orderId: this.orderId })
        .then(res => {
          // 仅在前一个路由为外站或者澄清文件时会调用次方法
          // 此时不应当使用澄清前的电子标书附件
          this.getKpbBiddingFile(res.data.biddingFileId, true)
        })
        .catch(err => {
          console.log('获取招标单详情失败：', err)
          if (err?.message) this.$message.error(err.message)
        })
    },

    /**
     * 导出评审办法excel
     */
    async exportExcel(readOnly) {
      if (!readOnly) await this.onSave()

      if (!this.kpbFileId) return
      const ids = {
        fileId: this.kpbFileId
      }
      window.open(exportReviewWaysExcelApi(ids), '_blank')
    },

    getReviewWayOption(option) {
      this.$emit('getReviewWayOption', option)
    },

    /**
     * 评审模式改变，通知父级，传递评审模式
     */
    onChangeModel(model) {
      this.$emit('changeModel', model)
    }
  }
}
</script>

<style scoped lang="scss">
.page-footer {
  width: 100%;
  height: 56px;
  background: $white-background-color;
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 99;
  border-top: 1px solid $border-color;
  box-sizing: border-box;
}
</style>
